import type { Project, Master, TmCoInfo } from './types';
import type { Bond, Withdraw } from './terms';
import { keepNewToken } from '../tokenRefresh';

const BASE = '/api/venture';

function token(): string {
  return localStorage.getItem('token') || '';
}

interface Resp<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token() };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(BASE + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  // 세션 만료·프록시 오류는 JSON 이 아니라 HTML 로 온다 — res.json() 이 SyntaxError 를 던져 원인이 묻히던 것
  if (res.status === 401) throw new Error('로그인이 만료됐습니다 — 다시 로그인한 뒤 이어서 작업하세요 (입력 중인 내용은 저장되지 않았습니다)');
  let j: Resp<T>;
  try { j = (await res.json()) as Resp<T>; }
  catch { throw new Error('서버 응답 오류 (' + res.status + ') — 잠시 후 다시 시도하세요'); }
  if (!j.success) throw new Error(j.error || '요청 실패 (' + res.status + ')');
  return j.data as T;
}

export interface Row {
  id: string;
  org: string;
  step: number;
  office?: 'roum' | 'atv';   // 목록에서만(서버 계산 · 2026-10-18)
  /** 조합에 든 회사(업체 id · 이름) — 목록에서만 · 업무 등록이 「이미 등록한 조합」을 찾는다(2026-10-18) */
  company_ids?: string[];
  company_names?: string[];
  data?: Project;
  created_at?: string;
  updated_at?: string;
}

export function listProjects(): Promise<Row[]> {
  return req<Row[]>('GET', '/projects');
}

export function getProject(id: string): Promise<Row> {
  return req<Row>('GET', '/projects/' + id);
}

export function createProject(p: Project): Promise<Row> {
  return req<Row>('POST', '/projects', { org: p.org, step: p.step, data: p });
}

export function updateProject(id: string, p: Project): Promise<Row> {
  return req<Row>('PUT', '/projects/' + id, { org: p.org, step: p.step, data: p });
}

/** 그룹 지정 · 빼기(group null) · 이름 바꾸기 — 서버는 data.group 한 칸만 고친다 (2026-10-08) */
export function setGroup(ids: string[], group: string | null): Promise<{ updated: number; group: string | null }> {
  return req<{ updated: number; group: string | null }>('PATCH', '/projects/group', { ids, group });
}

/** 별표(신경 써야 하는 조합) — 서버는 data.star 한 칸만 (2026-10-19) */
export function setStar(ids: string[], on: boolean): Promise<{ updated: number; on: boolean }> {
  return req<{ updated: number; on: boolean }>('PATCH', '/projects/star', { ids, on });
}

/** LP 후보 메모 (2026-10-08) — 조합에 LP 가 필요할 때 매칭할 회사 · 소개자 · 연락처 */
export type LpStatus = 'wait' | 'matched' | 'hold' | 'done';
export interface Lp {
  id: string;
  company: string;
  contact: string;
  phone: string;
  referrer: string;
  referrer_org: string;
  referrer_phone: string;
  amount: string;
  status: LpStatus;
  project_id: string | null;
  memo: string;
  collect_pid?: string | null;        // 「정보 받기 링크」 포털 (161, 2026-10-12)
  moved_project_id?: string | null;   // 이 조합의 LP 로 옮겨 넣었음
  /** 이체 구분 · 사채 처리 — 이 LP 가 원하는 쪽(미정 '') · 2026-10-28 · venture/terms */
  withdraw?: Withdraw;
  bond?: Bond;
  office?: 'roum' | 'atv' | null;    // 적은 곳(174, 2026-10-18) — 올댓벤처스 어드민은 'atv' 만 받는다 · 로움은 모두
  portal?: { total: number; done: number };   // 받은 자료 (서버 계산)
  created_at?: string;
  updated_at?: string;
}
export type LpInput = Partial<Omit<Lp, 'id' | 'created_at' | 'updated_at' | 'portal' | 'collect_pid' | 'office'>>;
export function listLp(): Promise<Lp[]> { return req<Lp[]>('GET', '/lp'); }
export function createLp(p: LpInput): Promise<Lp> { return req<Lp>('POST', '/lp', p); }
export function updateLp(id: string, p: LpInput): Promise<Lp> { return req<Lp>('PUT', '/lp/' + id, p); }
/** LP 후보 「정보 받기 링크」 — 조합 LP 카드와 같은 항목으로 포털을 미리 만든다(있으면 그대로) */
export function createLpPortal(id: string, items: CollectItemPayload[], brand: 'roum' | 'atv'): Promise<{ collect_pid: string; created: boolean }> {
  return req<{ collect_pid: string; created: boolean }>('POST', '/lp/' + id + '/portal', { items, brand });
}
/** LP 후보 「항목 편집」 — 지금 포털 항목(없으면 빈 배열) · 저장(없으면 만들고 · 있으면 동기화) (2026-11-01) */
export interface LpPortalGuide { type: string; label: string; url?: string; fileName?: string; filePath?: string; s3Key?: string }
export interface LpPortalItem { clientId: string; label: string; type: string; required: boolean; sensitive: boolean; section: string | null; description: string; guides: LpPortalGuide[] }
export function getLpPortalItems(id: string): Promise<{ collect_pid: string; items: LpPortalItem[] }> { return req('GET', '/lp/' + id + '/portal/items'); }
export function saveLpPortalItems(id: string, items: CollectItemPayload[], brand: 'roum' | 'atv'): Promise<{ collect_pid: string; created: boolean; total: number; kept?: number }> { return req('PUT', '/lp/' + id + '/portal/items', { items, brand }); }
export function deleteLp(id: string): Promise<{ deleted: boolean }> { return req<{ deleted: boolean }>('DELETE', '/lp/' + id); }

export interface TmCompany {
  id: string;
  name: string;
  biz_no?: string | null;
  ceo?: string | null;
  ceo_phone?: string | null;
}

export async function searchCompanies(q: string): Promise<TmCompany[]> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token() };
  const res = await fetch('/api/tm/companies?search=' + encodeURIComponent(q) + '&limit=8', { headers });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  const j = (await res.json()) as Resp<{ companies: TmCompany[] }>;
  if (!j.success) throw new Error(j.error || '업체 검색 실패');
  return (j.data && j.data.companies) || [];
}

export function linkCompany(projectId: string, companyId: string): Promise<unknown> {
  return req<unknown>('POST', '/projects/' + projectId + '/companies', { company_id: companyId, role: 'investee' });
}

/** 신규 법인을 업무관리(TM) 업체로 등록 → companyId 반환. 이미 있으면(biz_no/업체명 중복) 기존 id 반환. */
export async function createCompany(co: { name: string; biz_no?: string; ceo?: string; ceo_phone?: string; fp_ids?: string[]; wm_id?: string | null }): Promise<string | null> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' };
  const res = await fetch('/api/tm/companies', { method: 'POST', headers, body: JSON.stringify({ ...co, source: '벤처등록' }) });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  const j = (await res.json()) as { success?: boolean; data?: { id?: string }; existing?: { id?: string } };
  if (res.status === 409 && j.existing && j.existing.id) return j.existing.id;
  if (!res.ok || !j.success) return null;
  return (j.data && j.data.id) || null;
}

/** FP를 휴대폰(고유키)/이름으로 찾고 없으면 TM에 생성 → fpId. 실패해도 등록은 진행(null). */
export async function ensureFp(fp: { name: string; phone?: string; company?: string }): Promise<string | null> {
  const name = (fp.name || '').trim();
  if (!name) return null;
  const digits = (fp.phone || '').replace(/[^0-9]/g, '');
  try {
    const list = await listFps();
    let found = digits ? list.find((f) => (f.phone || '').replace(/[^0-9]/g, '') === digits) : undefined;
    if (!found) found = list.find((f) => f.name === name && (!fp.company || f.company === fp.company));
    if (found) return found.id;
    const headers: Record<string, string> = { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' };
    const res = await fetch('/api/tm/fps', { method: 'POST', headers, body: JSON.stringify({ name, phone: fp.phone || '', company: fp.company || '' }) });
    const j = (await res.json()) as { success?: boolean; data?: { id?: string } };
    if (res.ok && j.success && j.data && j.data.id) return j.data.id;
    if (res.status === 409 && digits) {
      const l2 = await listFps();
      const f2 = l2.find((f) => (f.phone || '').replace(/[^0-9]/g, '') === digits);
      if (f2) return f2.id;
    }
  } catch (e) { /* 연동 실패해도 등록 진행 */ }
  return null;
}

/** WM을 이름으로 찾고 없으면 TM에 생성 → wmId. */
export async function ensureWm(wm: { name: string; company?: string }): Promise<string | null> {
  const name = (wm.name || '').trim();
  if (!name) return null;
  try {
    const list = await listWms();
    const found = list.find((w) => w.name === name);
    if (found) return found.id;
    const headers: Record<string, string> = { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' };
    const res = await fetch('/api/tm/wms', { method: 'POST', headers, body: JSON.stringify({ name, company: wm.company || '' }) });
    const j = (await res.json()) as { success?: boolean; data?: { id?: string } };
    if (res.ok && j.success && j.data && j.data.id) return j.data.id;
  } catch (e) { /* 무시 */ }
  return null;
}

export function deleteProject(id: string): Promise<unknown> {
  return req<unknown>('DELETE', '/projects/' + id);
}

async function tmGet<T>(path: string): Promise<T> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token() };
  const res = await fetch('/api/tm' + path, { headers });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  const j = (await res.json()) as Resp<T>;
  if (!j.success) throw new Error(j.error || '요청 실패');
  return j.data as T;
}

export interface TmFp { id: string; name: string; phone?: string; email?: string; company?: string; }
export function listFps(): Promise<TmFp[]> {
  return tmGet<TmFp[]>('/fps');
}
export interface TmWm { id: string; name: string; phone?: string; company?: string; }
export function listWms(): Promise<TmWm[]> {
  return tmGet<TmWm[]>('/wms');
}
export async function listInsuranceCompanies(): Promise<string[]> {
  const rows = await tmGet<{ name?: string }[]>('/insurance-companies');
  // 중복 제거(같은 보험사명 여러 행이 드롭다운에 중복 표시되던 문제) — 첫 등장 순서 유지
  // (target es5 + downlevelIteration 미설정 → Set 스프레드 대신 Array.from 사용)
  return Array.from(new Set(rows.map((r) => r.name || '').filter(Boolean)));
}

/* /api/templates 는 success 래퍼 없이 원본 JSON을 반환 */
async function rawReq<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token() };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch('/api/templates' + path, {
    method, headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  const j = (await res.json()) as T & { success?: boolean; error?: string };
  if (!res.ok || (j && (j as { success?: boolean }).success === false)) {
    throw new Error(((j as { error?: string }) && (j as { error?: string }).error) || '요청 실패');
  }
  return j as T;
}

export interface TplGuide { id?: string; type?: string; label?: string; url?: string; fileName?: string; filePath?: string; s3Key?: string; }
export interface TplItem {
  id?: string; label?: string; description?: string; required?: boolean;
  sensitive?: boolean; is_sensitive?: boolean; type?: string;
  options?: unknown;
  extras?: { maps?: string[] } | null; guides?: TplGuide[];
}
export interface TemplateRow { id: string; name: string; itemCount: number; }
export interface TplDetail { id: string; name: string; items: TplItem[]; }

export function listTemplates(): Promise<TemplateRow[]> {
  return rawReq<TemplateRow[]>('GET', '');
}
export function getTemplate(id: string): Promise<TplDetail> {
  return rawReq<TplDetail>('GET', '/' + id);
}
export function createTemplate(payload: { name: string; items: unknown[] }): Promise<TplDetail> {
  return rawReq<TplDetail>('POST', '', payload);
}

/* ── 계정별 디폴트(마지막 사용) 자료수집 템플릿 ── */
export function getDefaultTemplate(): Promise<{ id: string | null }> {
  return req<{ id: string | null }>('GET', '/default-template');
}
export function setDefaultTemplate(id: string | null): Promise<{ id: string | null }> {
  return req<{ id: string | null }>('PUT', '/default-template', { id });
}

/* ── 법인 설립연도(Popbill, companies 캐시) → 주요경력 자동 ── */
export function fetchBizYear(bizNo: string): Promise<{ established_year: number | null; established_date?: string | null }> {
  return req<{ established_year: number | null; established_date?: string | null }>('GET', '/biz-year/' + encodeURIComponent(bizNo));
}

/* ── 실문서 생성/업로드/다운로드 ── */
export interface GenDocResult { key: string; doc: string; pdf: string; s3doc: string; s3pdf: string | null; converted: boolean; }
/** extra — 은행 안내장(bankguide)이 쓰는 지금 화면 값: 투자법인(cos) · 직접 고친 진행 순서(bankForm, null=기본값) (2026-10-31) */
export function genDocs(projectId: string, keys: string[], master: Master, regDate?: string, extra?: { cos?: unknown[]; bankForm?: string | null }): Promise<{ results: GenDocResult[] }> {
  return req<{ results: GenDocResult[] }>('POST', '/projects/' + projectId + '/gendocs', { keys, master, regDate, ...(extra || {}) });
}

/** 은행 안내장 진행 순서(2단계~) 기본값 — 지금 마스터 · 투자법인으로 (편집 화면 · 2026-10-31) */
export interface BankStep { title: string; desc: string }
export function bankguideSteps(projectId: string, master: Master, cos: unknown[]): Promise<{ steps: BankStep[] }> {
  return req<{ steps: BankStep[] }>('POST', '/projects/' + projectId + '/bankguide-steps', { master, cos, bankForm: null });
}

/* ── 사채(전환사채) 법인별 생성 + ZIP ── */
export interface BondDocResult { key: string; coId: string; doc: string; pdf: string; s3doc: string; s3pdf: string | null; converted: boolean; }
export function genBondDocs(projectId: string, coId: string, keys: string[], master: Master): Promise<{ results: BondDocResult[] }> {
  return req<{ results: BondDocResult[] }>('POST', '/projects/' + projectId + '/bonddocs', { coId, keys, master });
}
export interface BondZipAttach { name: string; s3Key?: string; uploadId?: string; }
// 사채서류 서명완료본/수정본 S3키 (ZIP에 원본 대신 이 파일을 사용)
export interface BondZipBondDoc { key: string; s3doc?: string; s3pdf?: string; }
/** regbook: 조합 등록원부(8단계 · 화면에 올라간 지금 파일) — 서버에 저장되기 전이어도 ZIP 에 넣는다(2026-11-02) */
export function bondZip(projectId: string, coId: string, master: Master, attachments?: BondZipAttach[], bondDocs?: BondZipBondDoc[], regbook?: { s3Key: string; name: string } | null): Promise<{ url: string; name: string }> {
  return req<{ url: string; name: string }>('POST', '/projects/' + projectId + '/bondzip', { coId, master, attachments: attachments || [], bondDocs: bondDocs || [], regbook: regbook || null });
}

/* ── 결성계획서 대비 변경내역 생성 ── */
export interface ChangeMember { name: string; share: string; }
export interface ChangeInput { date?: string; outs: ChangeMember[]; ins: ChangeMember[]; }
export function genChangeDoc(projectId: string, master: Master, change: ChangeInput): Promise<{ results: GenDocResult[] }> {
  return req<{ results: GenDocResult[] }>('POST', '/projects/' + projectId + '/changedoc', { master, change });
}

/* ── 자금운용현황 [별지 제9호] (15줄 결산보고, 2026-09-24) — 금액은 원 단위, 백엔드가 백만원 환산·소계 계산 ── */
export type FundPair = [number, number];   // [전년말까지 누계, 금년도 실적]
export interface FundCompanyRow { name: string; tel: string; ceo: string; addr: string; capital: number; founded: string; vno: string; date: string; stk: number; cb: number; bw: number; safe: number; }
export interface FundStatusBody {
  year: number; org: string;
  a: Record<string, FundPair>;
  notes: Record<string, string>;
  b: Record<string, { d1: string; d2: string; n: string }>;
  c: { gen: { name: string; org: string; date: string; bal: number; n: string }; etc: { name: string; org: string; date: string; bal: number; n: string } };
  d: FundCompanyRow[];
}
/** 연동된 업무관리 업체 정보 (13단계 VICS 자동 채움) */
export function getProjectCompanies(projectId: string): Promise<TmCoInfo[]> {
  return req<TmCoInfo[]>('GET', '/projects/' + projectId + '/companies');
}

export function genFundStatus(projectId: string, body: FundStatusBody): Promise<{ result: GenDocResult }> {
  return req<{ result: GenDocResult }>('POST', '/projects/' + projectId + '/fundstatus', body);
}

export interface UpDocResult { doc: string; pdf: string; s3doc: string | null; s3pdf: string | null; converted: boolean; }
export async function uploadDoc(projectId: string, key: string, kind: 'edit' | 'seal' | 'pdfonly', file: File): Promise<UpDocResult> {
  const fd = new FormData();
  fd.append('kind', kind);
  fd.append('file', file);
  const res = await fetch(BASE + '/projects/' + projectId + '/docs/' + key + '/upload', {
    method: 'POST',
    headers: { Authorization: 'Bearer ' + token() },
    body: fd,
  });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  const j = (await res.json()) as Resp<UpDocResult>;
  if (!j.success) throw new Error(j.error || '업로드 실패');
  return j.data as UpDocResult;
}

export async function s3upload(projectId: string, file: File): Promise<{ key: string; name: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(BASE + '/projects/' + projectId + '/s3file', {
    method: 'POST',
    headers: { Authorization: 'Bearer ' + token() },
    body: fd,
  });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  const j = (await res.json()) as Resp<{ key: string; name: string }>;
  if (!j.success) throw new Error(j.error || '업로드 실패');
  return j.data as { key: string; name: string };
}

export async function fileUrl(s3key: string, name: string, inline?: boolean): Promise<string> {
  const q = '?key=' + encodeURIComponent(s3key) + '&name=' + encodeURIComponent(name) + (inline ? '&inline=1' : '');
  const r = await req<{ url: string }>('GET', '/files' + q);
  return r.url;
}

/** 벤처투자확인서 생성 (11단계 법인별) — S3 저장 후 docver 버전 메타 반환 (표준 문서 흐름) */
export async function genInvestCert(projectId: string, coId: string, form: {
  type: 'pref' | 'cb' | 'safe'; subType: string; payDate: string; shares?: string;
  secured?: string; amount: string; recovered?: string; issueDate: string;
}): Promise<{ result: GenDocResult }> {
  return req<{ result: GenDocResult }>('POST', '/projects/' + projectId + '/investcert', { coId, form });
}

/** 동일출처 스트리밍으로 파일 blob 다운로드(폴더 일괄저장용 — S3 CORS 불필요) */
export async function fetchFileBlob(s3key: string, name: string): Promise<Blob> {
  const q = '?key=' + encodeURIComponent(s3key) + '&name=' + encodeURIComponent(name);
  const res = await fetch(BASE + '/files/blob' + q, { headers: { Authorization: 'Bearer ' + token() } });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  if (!res.ok) throw new Error('다운로드 실패 (' + res.status + ')');
  return res.blob();
}

/* ── 자료수집 1단계 실연동: 포털 생성 · 결과 조회 · 발송 · 제출물 승인 ── */
export interface CollectItemPayload {
  label: string; type: string; required: boolean; sensitive?: boolean;
  section: string | null; description?: string;
  guides?: { type?: string; label?: string; url?: string; fileName?: string; filePath?: string; s3Key?: string }[];
  clientId?: string;
}
export interface CreatePortalPayload {
  name: string; biz_no?: string; ceo?: string; ceo_phone?: string; ceo_email?: string;
  companyId?: string | null; label?: string; items: CollectItemPayload[];
  brand?: 'roum' | 'atv';   // 고객 화면 로고 (2026-09-11)
}
export function createCollectPortal(projectId: string, payload: CreatePortalPayload): Promise<{ project_id: string; items_count: number }> {
  return req<{ project_id: string; items_count: number }>('POST', '/projects/' + projectId + '/collect/create-portal', payload);
}
export interface SyncResult { project_id: string; updated: number; added: number; removed: number; kept?: number; }
/** 진행 주체 변경 → 이 프로젝트가 만든 자료수집 포털 전부의 고객 화면 로고를 한 번에 (2026-09-24) */
export function setPortalBrand(projectId: string, brand: 'roum' | 'atv', cpids: string[]): Promise<{ updated: number }> {
  return req<{ updated: number }>('PUT', '/projects/' + projectId + '/collect/brand', { brand, cpids });
}
export function syncCollectPortal(projectId: string, collectProjectId: string, items: CollectItemPayload[], who?: { name?: string; ceo?: string; brand?: 'roum' | 'atv'; companyId?: string | null }): Promise<SyncResult> {
  return req<SyncResult>('PUT', '/projects/' + projectId + '/collect/' + collectProjectId + '/sync', { items, name: who ? who.name : undefined, ceo: who ? who.ceo : undefined, brand: who ? who.brand : undefined, companyId: who ? who.companyId : undefined });
}
/** 13단계 첨부 서류(등기부등본·사업자등록증·벤처기업확인서) 자동 인식 — 글자 PDF 는 정규식, 스캔·사진은 AI (2026-09-24) */
export interface VicsExtractResult { doc: 'regcb' | 'biz' | 'vcert'; fields: Record<string, string>; source: 'text' | 'ai' | 'text+ai' | 'none'; warnings: string[]; }
export function vicsExtract(projectId: string, body: { doc: 'regcb' | 'biz' | 'vcert'; s3key?: string; uploadId?: string; name: string }): Promise<VicsExtractResult> {
  return req<VicsExtractResult>('POST', '/projects/' + projectId + '/vics-extract', body);
}
export function uploadPreview(uploadId: string, dl?: boolean): Promise<{ url: string }> {
  return req<{ url: string }>('GET', '/upload-preview/' + uploadId + (dl ? '?dl=1' : ''));
}

/** 첨부 파일을 이름으로 S3에서 찾아 URL 발급 — 키 미저장 옛 업로드 다운로드 복구용 */
export function findAttach(projectId: string, name: string, dl?: boolean): Promise<{ url: string; key: string }> {
  return req<{ url: string; key: string }>('GET', '/projects/' + projectId + '/find-attach?name=' + encodeURIComponent(name) + (dl ? '&dl=1' : ''));
}

export interface CollectResultRow {
  item_id: string; title: string; type: string; required: boolean;
  section: string | null; status: 'approved' | 'rejected' | 'pending' | 'none';
  value: string; uploadId?: string | null; files: { id: string; name: string; status: string }[];
}
export function getCollectResults(projectId: string, collectProjectId: string): Promise<{ project_id: string; status: string; rows: CollectResultRow[] }> {
  return req<{ project_id: string; status: string; rows: CollectResultRow[] }>('GET', '/projects/' + projectId + '/collect/results/' + collectProjectId);
}

/* 발송 — /api/notifications/send (성공 응답에 success 래퍼 없음) */
export interface NotifyRecipient { phone?: string; email?: string; name?: string; msgType?: 'A' | 'B'; }
export interface NotifySendResult { name: string; phone: string; email: string; smsOk: boolean | null; smsMsg: string | null; emailOk: boolean | null; emailMsg: string | null; }
export interface NotifySendPayload {
  projectId: string; type: string; recipients: NotifyRecipient[];
  channel: 'sms' | 'email' | 'both'; senderNum?: string;
  messageSms?: string; messageEmail?: string;
}
export async function sendCollectNotification(payload: NotifySendPayload): Promise<{ results: NotifySendResult[] }> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' };
  const res = await fetch('/api/notifications/send', { method: 'POST', headers, body: JSON.stringify(payload) });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  const j = (await res.json()) as { results?: NotifySendResult[]; success?: boolean; error?: string };
  if (!res.ok || j.success === false) throw new Error(j.error || '발송 실패');
  return { results: j.results || [] };
}

/* 제출물 승인/취소 — /api/uploads/:id/... */
async function uploadsReq(method: string, path: string): Promise<void> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token() };
  const res = await fetch('/api/uploads' + path, { method, headers });
  const nt = res.headers.get('X-New-Token');
  keepNewToken(nt);
  const j = (await res.json()) as Resp<unknown>;
  if (!j.success) throw new Error(j.error || '처리 실패');
}
export function approveUpload(uploadId: string): Promise<void> {
  return uploadsReq('PUT', '/' + uploadId + '/approve');
}
export function unapproveUpload(uploadId: string): Promise<void> {
  return uploadsReq('PUT', '/' + uploadId + '/unapprove');
}
